import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BdcDashboardHistogramBucket } from "../../../../types/bdcDashboard.type";
import { SURFACE } from "../../../common/designTokens";
import { pcs } from "../../format";
import { CHART_COLORS } from "../constants";

const HEIGHT = 200;

/** `3` → `3 วัน`, `8+` → `8+ วัน`. */
export const bucketTick = (bucket: BdcDashboardHistogramBucket) => `${bucket.label} วัน`;

/**
 * Stores by consecutive days without an order up to the selected day. One series (stores);
 * the darker bars are the buckets at or past the lapse threshold.
 */
export default function StreakHistogram({ buckets }: Readonly<{ buckets: BdcDashboardHistogramBucket[] }>) {
  const data = buckets.map((bucket) => ({ ...bucket, tick: bucketTick(bucket) }));
  if (data.every((bucket) => bucket.stores === 0)) {
    return (
      <Box sx={{ height: HEIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          ทุกร้านในฐานสั่งในวันที่เลือก
        </Typography>
      </Box>
    );
  }
  return (
    <Box role="img" aria-label="จำนวนร้านแยกตามจำนวนวันที่ไม่ได้สั่งติดกัน">
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <BarChart data={data} margin={{ top: 20, right: 4, left: 4, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
          <XAxis
            dataKey="tick"
            tick={{ fill: CHART_COLORS.ink, fontSize: 12 }}
            axisLine={{ stroke: CHART_COLORS.axis }}
            tickLine={false}
            interval={0}
          />
          <YAxis hide domain={[0, "auto"]} />
          <Tooltip
            cursor={{ fill: "rgba(54,58,68,0.06)" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0].payload as BdcDashboardHistogramBucket;
              return (
                <Box
                  sx={{
                    bgcolor: "#fff",
                    border: `1px solid ${SURFACE.line}`,
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} display="block">
                    ไม่สั่งติดกัน {row.label} วัน
                  </Typography>
                  <Typography variant="caption" display="block">
                    {pcs(row.stores)} ร้าน{row.is_lapsed ? " · เข้าเกณฑ์ขาดสั่ง" : ""}
                  </Typography>
                </Box>
              );
            }}
          />
          <Bar dataKey="stores" name="ร้าน" radius={[4, 4, 0, 0]} maxBarSize={60} isAnimationActive={false}>
            {data.map((bucket) => (
              <Cell key={bucket.label} fill={bucket.is_lapsed ? CHART_COLORS.lapsed : CHART_COLORS.shortLapse} />
            ))}
            <LabelList
              dataKey="stores"
              position="top"
              offset={6}
              style={{ fontSize: 12, fill: SURFACE.tx, fontVariantNumeric: "tabular-nums" }}
              formatter={(value: unknown) => pcs(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
